import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { DashboardSegmentedToggleOption } from './models/dashboard-segmented-toggle-option';

@Component({
    selector: 'app-dashboard-segmented-toggle',
    imports: [FormsModule, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DashboardSegmentedToggleComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './dashboard-segmented-toggle.component.html',
})
export class DashboardSegmentedToggleComponent<T>
    implements ControlValueAccessor
{
    @Input() options: DashboardSegmentedToggleOption<T>[] = [];

    private selectedSet = new Set<T>();

    private readonly compareWith: (a: T, b: T) => boolean = (a, b) =>
        a === (b as T);

    constructor(private cdr: ChangeDetectorRef) {}

    private onChange: (value: T | T[] | null) => void = () => {};
    private onTouched: () => void = () => {};

    isSelected(value: T): boolean {
        for (const v of this.selectedSet) {
            if (this.compareWith(v, value)) return true;
        }

        return false;
    }

    onValueSelected(value: T): void {
        this.handleSingleSelection(value);
        this.onTouched();
    }

    writeValue(value: T | T[] | null): void {
        this.selectedSet.clear();

        if (Array.isArray(value)) {
            value.forEach((v) => this.selectedSet.add(v));
        } else if (value != null) {
            this.selectedSet.add(value);
        }

        this.cdr.detectChanges();
    }

    registerOnChange(fn: (value: T | T[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private handleMultipleSelection(value: T): void {
        if (this.isSelected(value)) {
            this.deleteFromSet(value);
        } else {
            this.selectedSet.add(value);
        }
        this.emit();
    }

    private handleSingleSelection(value: T): void {
        this.selectedSet.clear();
        this.selectedSet.add(value);
        this.emit();
    }

    private emit(): void {
        const [first] = this.selectedSet;
        this.onChange(first ?? null);
    }

    private deleteFromSet(value: T): void {
        for (const v of this.selectedSet) {
            if (this.compareWith(v, value)) {
                this.selectedSet.delete(v);
                return;
            }
        }
    }
}
